import {
  formatParisDateTime,
  RECHARGE_RUN_STATUS,
  type Paginated,
  type RechargeExportListItem,
} from '@flux/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, RotateCw } from 'lucide-react';
import { useEffect, useRef, type JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { downloadRechargeExport, fetchRechargeExports, startRechargeExport } from './api.js';
import { formatCount } from './money.js';
import {
  isRechargeTokenMissing,
  RECHARGE_NOT_CONFIGURED_EMPTY_STATE,
  useRechargeSummary,
} from './recharge-status.js';

const PAGE_SIZE = 50;
/** Rafraîchissement de la liste tant qu'un export tourne (`06` §8.4). */
const POLL_INTERVAL_MS = 2_000;
const NUMERIC_COLUMNS = ['subscriptions', 'rows', 'missingShopifyTitles'] as const;
const GENERATE_LABEL = 'Générer l’export';
const GENERATE_REASON_ID = 'recharge-export-generate-reason';

function hasRunningExport(page: Paginated<RechargeExportListItem> | undefined): boolean {
  return page?.items.some((item) => item.status === 'running') ?? false;
}

function Count({ value }: { value: number | null }): JSX.Element {
  return value === null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="whitespace-nowrap tabular-nums">{formatCount(value)}</span>
  );
}

interface RowActionsProps {
  item: RechargeExportListItem;
  canGenerate: boolean;
  isGenerating: boolean;
  onRetry: () => void;
}

/** « Télécharger » pour un export terminé, « Réessayer » pour un échec, rien pendant l'export. */
function ExportRowActions({ item, canGenerate, isGenerating, onRetry }: RowActionsProps): JSX.Element | null {
  if (item.status === 'running') {
    return null;
  }
  if (item.status === 'failed') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={!canGenerate}
        isLoading={isGenerating}
        onClick={onRetry}
      >
        <RotateCw aria-hidden="true" />
        Réessayer
      </Button>
    );
  }
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={!item.downloadable}
      onClick={() => {
        downloadRechargeExport(item.id);
      }}
    >
      <Download aria-hidden="true" />
      Télécharger
    </Button>
  );
  // Un bouton désactivé ne reçoit pas le survol : la raison est portée par son conteneur.
  return item.downloadable ? (
    button
  ) : (
    <span className="inline-flex" title="Fichier expiré ou indisponible">
      {button}
    </span>
  );
}

/**
 * Onglet Exports de l'écran Abonnements (`06` §4, §8.4) : génère le CSV des
 * produits par abonnement actif et liste les générations. Lecture seule chez
 * Recharge, sans mode ni confirmation ; un seul export à la fois.
 */
export function ExportsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const { page, setPage } = useDataTableUrlState();
  const summary = useRechargeSummary();
  const tokenMissing = isRechargeTokenMissing(summary.data);
  /** Exports lancés depuis cet écran, en attente de leur toast de fin. */
  const startedHere = useRef(new Set<string>());

  const listParams = { page, pageSize: PAGE_SIZE };
  const query = useQuery({
    queryKey: queryKeys.subscriptions.exports(listParams),
    queryFn: () => fetchRechargeExports(listParams),
    placeholderData: keepPreviousData,
    refetchInterval: (current) => (hasRunningExport(current.state.data) ? POLL_INTERVAL_MS : false),
  });

  const generate = useMutation({
    mutationFn: startRechargeExport,
    onSuccess: (item) => {
      startedHere.current.add(item.id);
      // Le nouvel export arrive en tête de liste : sans la première page, ni
      // suivi, ni bouton désactivé, ni toast de fin.
      if (page !== 1) {
        setPage(1);
      }
    },
    onError: (error) => {
      toast.fromError(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.exportsAll });
    },
  });

  useEffect(() => {
    for (const item of query.data?.items ?? []) {
      if (!startedHere.current.has(item.id) || item.status === 'running') {
        continue;
      }
      startedHere.current.delete(item.id);
      if (item.status === 'succeeded' && item.downloadable) {
        toast.success('Export prêt', {
          action: {
            label: 'Télécharger',
            onClick: () => {
              downloadRechargeExport(item.id);
            },
          },
        });
      }
    }
  }, [query.data]);

  const isRunning = hasRunningExport(query.data) || generate.isPending;
  const disabledReason = tokenMissing
    ? 'Ajoutez le token API dans Paramètres'
    : hasRunningExport(query.data)
      ? 'Un export est en cours'
      : null;
  const canGenerate = disabledReason === null && !generate.isPending;

  function startExport(): void {
    generate.mutate();
  }

  const helper = createDataTableColumnHelper<RechargeExportListItem>();
  const columns = helper.columns([
    helper.accessor('startedAt', {
      id: 'startedAt',
      header: 'Généré le',
      cell: (info) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatParisDateTime(new Date(info.getValue()))}
        </span>
      ),
    }),
    helper.accessor('startedBy', {
      id: 'startedBy',
      header: 'Par',
      cell: (info) => info.getValue()?.name ?? <span className="text-muted-foreground">—</span>,
    }),
    helper.accessor('status', {
      id: 'status',
      header: 'Statut',
      cell: ({ row }) => {
        const { status, error } = row.original;
        return (
          <span className="flex min-w-0 flex-col items-start gap-1">
            <StatusBadge registry={RECHARGE_RUN_STATUS} code={status} />
            {status === 'failed' && error !== null ? (
              <span className="block max-w-xs truncate text-xs text-status-red-fg" title={error}>
                {error}
              </span>
            ) : null}
          </span>
        );
      },
    }),
    helper.accessor('subscriptions', {
      id: 'subscriptions',
      header: 'Abonnements',
      cell: (info) => <Count value={info.getValue()} />,
    }),
    helper.accessor('rows', {
      id: 'rows',
      header: 'Lignes',
      cell: (info) => <Count value={info.getValue()} />,
    }),
    helper.accessor('missingShopifyTitles', {
      id: 'missingShopifyTitles',
      header: 'Titres manquants',
      cell: (info) => <Count value={info.getValue()} />,
    }),
    helper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <ExportRowActions
          item={row.original}
          canGenerate={canGenerate}
          isGenerating={generate.isPending}
          onRetry={startExport}
        />
      ),
    }),
  ]);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Une ligne par produit de chaque abonnement actif · 1 à 3 minutes
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {disabledReason === null ? null : (
            <p id={GENERATE_REASON_ID} className="text-sm text-muted-foreground">
              {disabledReason}
            </p>
          )}
          <Button
            type="button"
            disabled={!canGenerate}
            isLoading={isRunning && !tokenMissing}
            aria-describedby={disabledReason === null ? undefined : GENERATE_REASON_ID}
            onClick={startExport}
          >
            {GENERATE_LABEL}
          </Button>
        </div>
      </div>
      <DataTable
        caption="Exports des abonnements actifs"
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={setPage}
        isLoading={query.isPending}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError ? query.error.message : 'Impossible de charger les exports.'
        }
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        emptyState={
          tokenMissing
            ? RECHARGE_NOT_CONFIGURED_EMPTY_STATE
            : {
                title: 'Aucun export',
                description: 'Générez la liste des produits par abonnement actif.',
                action: (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!canGenerate}
                    onClick={startExport}
                  >
                    {GENERATE_LABEL}
                  </Button>
                ),
              }
        }
      />
    </>
  );
}
